import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search, ChevronRight } from 'lucide-react';
import Header from './components/Header13';
import Footer from './components/Footer13';

const Orders13 = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const orders = [
        { id: 'KRG-001', date: '2026-03-10', status: 'delivered', total: 4999, items: [
            { name: 'Chanderi Silk Handloom Saree', quantity: 1, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=200' },
        ], trackingNumber: 'TRK123456789' },
        { id: 'KRG-002', date: '2026-03-15', status: 'shipped', total: 2499, items: [
            { name: 'Jaipur Block Print Kurta', quantity: 1, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=200' },
        ], trackingNumber: 'TRK987654321', expectedDelivery: '2026-03-20' },
        { id: 'KRG-003', date: '2026-03-18', status: 'processing', total: 8499, items: [
            { name: 'Patola Double Ikat Saree', quantity: 1, image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=200' },
        ] },
    ];

    const statusConfig = {
        delivered: { label: 'Delivered', icon: CheckCircle, color: '#16A34A', bg: '#F0FDF4' },
        shipped: { label: 'Shipped', icon: Truck, color: '#C2410C', bg: '#FFF7ED' },
        processing: { label: 'Processing', icon: Clock, color: '#CA8A04', bg: '#FEFCE8' },
    };

    const filteredOrders = orders.filter(o => {
        const matchSearch = o.id.toLowerCase().includes(searchTerm.toLowerCase());
        const matchFilter = filterStatus === 'all' || o.status === filterStatus;
        return matchSearch && matchFilter;
    });

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Mulish:wght@300;400;600&display=swap');`}</style>
            <div style={{ fontFamily: "'Mulish', sans-serif" }} className="min-h-screen bg-[#FBF8F4]">
                <Header />
                <main style={{ paddingTop: '72px' }}>
                    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px 80px' }}>
                        <h1 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#1C1917', marginBottom: '8px' }} className="lg:text-4xl">Your Orders</h1>
                        <p style={{ fontSize: '14px', color: '#78716C', marginBottom: '32px' }}>Track your artisan craft orders</p>

                        {/* Search & Filter */}
                        <div className="flex flex-col sm:flex-row gap-3 mb-8">
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#78716C' }} />
                                <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by order ID..."
                                    style={{ width: '100%', padding: '12px 16px 12px 44px', border: '1px solid #E7E0D6', borderRadius: '4px', backgroundColor: '#FBF8F4', fontSize: '14px', color: '#1C1917', outline: 'none' }}
                                    onFocus={(e) => { e.target.style.borderColor = '#C2410C'; }} onBlur={(e) => { e.target.style.borderColor = '#E7E0D6'; }} />
                            </div>
                            <div className="flex gap-2">
                                {['all', 'processing', 'shipped', 'delivered'].map(status => (
                                    <button key={status} onClick={() => setFilterStatus(status)}
                                        style={{
                                            padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize',
                                            backgroundColor: filterStatus === status ? '#C2410C' : '#F7F3ED',
                                            color: filterStatus === status ? '#FFF' : '#1C1917',
                                        }}>
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Orders List */}
                        {filteredOrders.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                                <Package size={48} style={{ color: '#E7E0D6', margin: '0 auto 16px' }} />
                                <p style={{ fontSize: '16px', color: '#78716C' }}>No orders found</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {filteredOrders.map(order => {
                                    const config = statusConfig[order.status];
                                    const StatusIcon = config.icon;
                                    return (
                                        <div key={order.id} style={{ backgroundColor: '#F7F3ED', borderRadius: '8px', padding: '20px', border: '1px solid #E7E0D6' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                                                <div>
                                                    <p style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '16px', fontWeight: 700, color: '#1C1917' }}>{order.id}</p>
                                                    <p style={{ fontSize: '12px', color: '#78716C' }}>Ordered on {new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', backgroundColor: config.bg }}>
                                                    <StatusIcon size={14} style={{ color: config.color }} />
                                                    <span style={{ fontSize: '12px', fontWeight: 600, color: config.color }}>{config.label}</span>
                                                </div>
                                            </div>

                                            {order.items.map((item, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                                                    <img src={item.image} alt={item.name} style={{ width: '56px', height: '70px', borderRadius: '4px', objectFit: 'cover' }} />
                                                    <div>
                                                        <p style={{ fontSize: '14px', color: '#1C1917', marginBottom: '2px' }}>{item.name}</p>
                                                        <p style={{ fontSize: '12px', color: '#78716C' }}>Qty: {item.quantity}</p>
                                                    </div>
                                                </div>
                                            ))}

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #E7E0D6' }}>
                                                <span style={{ fontSize: '16px', fontWeight: 600, color: '#1C1917' }}>{`\u20B9${order.total.toLocaleString()}`}</span>
                                                {order.trackingNumber && (
                                                    <button style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#C2410C', fontWeight: 600 }}>
                                                        Track Order <ChevronRight size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default Orders13;
